import { deleteField } from 'firebase/firestore';
import type { CuratedCollection } from '../../types';
import { extractYouTubeId, youTubeEmbedUrl } from '../../utils/youtube';
import { buildFacetFilters, FACET_LABELS } from './facetFilters';
import type { FacetKey } from './facetFilters';

export type CollectionType = 'filter' | 'manual';

/** Where a card takes the user. `'search'` is the wire default (field absent). */
export type CollectionTarget = 'search' | 'landing' | 'whatsNew';

/**
 * Targets that open a screen of their own and so need a search fallback for
 * builds that predate them: both always ship as `type: 'filter'` with a
 * non-empty `filterCriteria` (watchlock `curatedCollectionCompat` taps that
 * through to Advanced Search on an older client).
 */
export function needsSearchFallback(target: CollectionTarget): boolean {
  return target === 'landing' || target === 'whatsNew';
}

/** Landing pages render a carousel, so the ref list is capped. */
export const LANDING_REFERENCE_CAP = 20;

export interface NumericRange {
  min: string;
  max: string;
}

export interface ManualReference {
  id: string;
  reference?: string;
  brandId?: string;
  model?: string;
}

export interface EditorState {
  id: string | null;
  name: string;
  description: string;
  imageUrl: string;
  titleColor: string;
  showOverlay: boolean;
  targetType: CollectionTarget;
  type: CollectionType;
  facetSelections: { key: FacetKey; value: string }[];
  caseSize: NumericRange;
  waterResistance: NumericRange;
  query: string;
  manualReferences: ManualReference[];
  landingBodyHtml: string;
  articleId: string;
  videoUrl: string;
  requiresAppMinVersion: string;
  isActive: boolean;
  testMode: boolean;
  sortOrder: number;
}

export const emptyEditor: EditorState = {
  id: null,
  name: '',
  description: '',
  imageUrl: '',
  titleColor: '#FFFFFF',
  showOverlay: true,
  targetType: 'search',
  type: 'filter',
  facetSelections: [],
  caseSize: { min: '', max: '' },
  waterResistance: { min: '', max: '' },
  query: '',
  manualReferences: [],
  landingBodyHtml: '',
  articleId: '',
  videoUrl: '',
  requiresAppMinVersion: '',
  isActive: true,
  testMode: false,
  sortOrder: 0,
};

const NUMERIC_FIELDS = {
  caseSize: 'caseSizeMm',
  waterResistance: 'waterResistanceM',
} as const;

export function parseNumericFilters(filters: string[]): {
  caseSize: NumericRange;
  waterResistance: NumericRange;
  unknown: string[];
} {
  const caseSize: NumericRange = { min: '', max: '' };
  const waterResistance: NumericRange = { min: '', max: '' };
  const unknown: string[] = [];

  filters.forEach((raw) => {
    const f = raw.trim();
    const match = f.match(/^([A-Za-z0-9_]+)\s*(>=|<=|>|<|=)\s*(-?[\d.]+)$/);
    if (!match) {
      unknown.push(f);
      return;
    }
    const [, field, op, num] = match;
    let target: NumericRange | null = null;
    if (field === NUMERIC_FIELDS.caseSize) target = caseSize;
    else if (field === NUMERIC_FIELDS.waterResistance) target = waterResistance;
    if (!target) {
      unknown.push(f);
      return;
    }
    if (op === '>=' || op === '>') target.min = num;
    else if (op === '<=' || op === '<') target.max = num;
    else if (op === '=') {
      target.min = num;
      target.max = num;
    }
  });

  return { caseSize, waterResistance, unknown };
}

export function buildNumericFilters(state: EditorState): string[] {
  const filters: string[] = [];
  const push = (field: string, range: NumericRange) => {
    if (range.min.trim()) filters.push(`${field}>=${range.min.trim()}`);
    if (range.max.trim()) filters.push(`${field}<=${range.max.trim()}`);
  };
  push(NUMERIC_FIELDS.caseSize, state.caseSize);
  push(NUMERIC_FIELDS.waterResistance, state.waterResistance);
  return filters;
}

/**
 * Canonicalise a video URL to a YouTube embed URL.
 *
 * Returns null when the input is blank or is not a YouTube link, which the
 * caller reads as "reject". Articles already store their header video in embed
 * form, so a pre-filled URL passes through unchanged.
 */
export function normaliseVideoUrl(raw: string): string | null {
  const trimmed = raw.trim();
  if (!trimmed) return null;
  const videoId = extractYouTubeId(trimmed);
  return videoId ? youTubeEmbedUrl(videoId) : null;
}

export function collectionToEditor(c: CuratedCollection): EditorState {
  const facetSelections: { key: FacetKey; value: string }[] = [];
  (c.filterCriteria?.facetFilters || []).forEach((group) => {
    group.forEach((entry) => {
      const idx = entry.indexOf(':');
      if (idx <= 0) return;
      const key = entry.slice(0, idx) as FacetKey;
      const value = entry.slice(idx + 1);
      if (key in FACET_LABELS) {
        facetSelections.push({ key, value });
      }
    });
  });

  const parsed = parseNumericFilters(c.filterCriteria?.numericFilters || []);

  return {
    id: c.id,
    name: c.name ?? '',
    description: c.description ?? '',
    imageUrl: c.imageUrl ?? '',
    titleColor: c.titleColor || '#FFFFFF',
    showOverlay: c.showOverlay !== false,
    targetType:
      c.targetType === 'landing' || c.targetType === 'whatsNew' ? c.targetType : 'search',
    type: c.type,
    facetSelections,
    caseSize: parsed.caseSize,
    waterResistance: parsed.waterResistance,
    query: c.filterCriteria?.query ?? '',
    manualReferences: (c.referenceIds || []).map((id) => ({ id })),
    landingBodyHtml: c.landingBodyHtml ?? '',
    articleId: c.articleId ?? '',
    videoUrl: c.videoUrl ?? '',
    requiresAppMinVersion: c.requiresAppMinVersion ?? '',
    isActive: c.isActive ?? true,
    testMode: c.testMode === true,
    sortOrder: c.sortOrder ?? 0,
  };
}

export function editorToPayload(state: EditorState): Partial<CuratedCollection> {
  const isLanding = state.targetType === 'landing';
  const isWhatsNew = state.targetType === 'whatsNew';
  const hasFallback = needsSearchFallback(state.targetType);

  // `saveCuratedCollection` strips `undefined` before writing, so omitting a
  // field means "leave whatever is there alone" — not "clear it". A
  // `deleteField()` sentinel is not `undefined`, so it survives the strip. Only
  // `updateDoc` accepts one though, so on create we omit as before.
  const clear = () =>
    state.id ? (deleteField() as unknown as undefined) : undefined;

  const base: Partial<CuratedCollection> = {
    name: state.name.trim(),
    description: state.description.trim() || undefined,
    imageUrl: state.imageUrl.trim() || undefined,
    titleColor:
      state.titleColor && state.titleColor.trim() && state.titleColor.trim().toUpperCase() !== '#FFFFFF'
        ? state.titleColor.trim()
        : undefined,
    showOverlay: state.showOverlay,
    // Landing cards always ship as `type: 'filter'` so any client that doesn't
    // understand `targetType` still renders the card and taps through to
    // ExploreAdvancedSearch via `filterCriteria`. See `validateEditor` for the
    // matching guarantee that `filterCriteria` is non-empty when landing.
    // What's New cards ship the same way, for the same reason (memo Q18).
    type: hasFallback ? 'filter' : state.type,
    // Test mode implies not-live. Forcing `isActive: false` is what keeps a
    // test card off every already-shipped client: they filter on `isActive`
    // alone and have never heard of `testMode`. The editor disables the Active
    // checkbox while test mode is on so the UI cannot claim otherwise.
    isActive: state.testMode ? false : state.isActive,
    testMode: state.testMode ? true : clear(),
    sortOrder: state.sortOrder,
  };

  const facetFilters = buildFacetFilters(state.facetSelections);
  const numericFilters = buildNumericFilters(state);
  const queryStr = state.query.trim();

  const filterCriteria: CuratedCollection['filterCriteria'] = {};
  if (facetFilters.length > 0) filterCriteria.facetFilters = facetFilters;
  if (numericFilters.length > 0) filterCriteria.numericFilters = numericFilters;
  if (queryStr) filterCriteria.query = queryStr;

  const referenceIds = state.manualReferences.map((r) => r.id);

  if (isLanding) {
    // The one mode that carries both. `referenceIds` is what the landing screen
    // renders; `filterCriteria` is the search a later client can fall back to.
    base.filterCriteria = filterCriteria;
    base.referenceIds = referenceIds;
  } else if (isWhatsNew || state.type === 'filter') {
    // What's New: `filterCriteria` is only the old-build fallback; the page
    // itself is built from the What's New config, never from this card.
    base.filterCriteria = filterCriteria;
    base.referenceIds = [];
  } else {
    base.referenceIds = referenceIds;
    base.filterCriteria = {};
  }

  // The one landing field that is not landing-only: a search-mode card renders
  // the linked article as a subtle chip on the Explore banner, so the id has to
  // survive a save in either mode. Blank still clears — that reads as "the
  // curator unlinked the article", which means the same thing in both modes.
  // A What's New card has no article chip — the page is fully config-driven.
  base.articleId = isWhatsNew ? clear() : state.articleId.trim() || clear();

  if (isLanding) {
    base.targetType = 'landing';
    base.landingBodyHtml = state.landingBodyHtml.trim() || clear();
    base.videoUrl = normaliseVideoUrl(state.videoUrl) ?? clear();
    base.requiresAppMinVersion = state.requiresAppMinVersion.trim() || clear();
  } else {
    // An absent `targetType` is what 'search' means on the wire. Clearing the
    // rest matters most for `requiresAppMinVersion`: left behind, it goes on
    // hiding a card that this collection no longer needs hidden.
    base.targetType = isWhatsNew ? 'whatsNew' : clear();
    base.landingBodyHtml = clear();
    base.videoUrl = clear();
    base.requiresAppMinVersion = clear();
  }

  return base;
}

function hasAnyFilter(state: EditorState): boolean {
  return (
    state.facetSelections.length > 0 ||
    buildNumericFilters(state).length > 0 ||
    state.query.trim().length > 0
  );
}

export interface EditorValidation {
  /** Blocks the save. */
  error?: string;
  /** Shown after a successful save. */
  warning?: string;
}

export function validateEditor(state: EditorState): EditorValidation {
  if (!state.name.trim()) return { error: 'Name is required' };

  if (state.targetType === 'whatsNew') {
    // Same rule as a landing card's fallback, same reason: on a build that
    // predates What's New the card still shows, and this search is the tap.
    return hasAnyFilter(state)
      ? {}
      : {
          error:
            "Older app versions fall back to this search — add at least one filter or a search query.",
        };
  }

  if (state.targetType !== 'landing') return {};

  const count = state.manualReferences.length;
  if (count > LANDING_REFERENCE_CAP) {
    const excess = count - LANDING_REFERENCE_CAP;
    return {
      error: `Landing pages allow at most ${LANDING_REFERENCE_CAP} references (currently ${count}). Remove ${excess}.`,
    };
  }

  if (state.videoUrl.trim() && !normaliseVideoUrl(state.videoUrl)) {
    return { error: 'Video URL must be a YouTube link.' };
  }

  const hasFilter = hasAnyFilter(state);

  // The filter is not decorative on a landing collection — it IS the fallback
  // any client without landing-page support taps through to. Blocked, not
  // warned: a landing card without a fallback is a dead click on every client
  // that predates the landing screen.
  if (!hasFilter) {
    return {
      error:
        'Older app versions fall back to this search — add at least one filter or a search query.',
    };
  }

  return {};
}

/**
 * Move one reference `delta` places within the list, returning a new array.
 *
 * Order is the whole point of the list: `referenceIds[]` is what the landing
 * screen renders, in array order, so this is the only control a curator has
 * over the sequence. A move that would leave the array returns the original
 * array unchanged rather than clamping to the ends — the caller disables
 * the first row's up arrow and the last row's down arrow, and a clamp would
 * quietly turn a mis-wired button into a no-op instead of a visible bug.
 */
export function moveReference(
  references: ManualReference[],
  index: number,
  delta: number,
): ManualReference[] {
  const target = index + delta;
  if (index < 0 || index >= references.length) return references;
  if (target < 0 || target >= references.length) return references;
  const next = [...references];
  const [moved] = next.splice(index, 1);
  next.splice(target, 0, moved);
  return next;
}
